import { useAppStore } from '@/stores/app'
import request from '@/utils/request'
import textHelpers from '@/utils/textHelpers'
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile'
import validationUtil from '@/utils/validationUtil'

interface Member {
  memberId: number
  memberFirstName: string
  memberEmail: string
  memberPhoneNumber: number
  memberPositionName: string
  memberComment: string
  groupName: string
  groupId: number
  shopsRelated: number[]
  notificationStrategy: number
  notificationStrategyName: string
  canUpdateMember: boolean
  email?: string
  phoneNumber?: number
  allowPhone?: boolean
  allowSms?: boolean
  allowWhatsapp?: boolean
  allowEmail?: boolean
  positionName?: string
  comment?: string
}

interface RawMember extends Partial<Member> {
  memberGroupId?: number
  memberGroupName?: string
  memberNotificationStrategy?: number
  memberNotificationStrategyName?: string
}

interface NotificationGroup {
  groupId: number
  groupName: string
  shopIds: number[]
}

interface Strategy {
  id: number
  name: string
}

const shouldEmitDebugWarnings = !import.meta.env.PROD

const normalizeMember = (member: RawMember): Member => {
  const resolvedGroupId = member.groupId ?? member.memberGroupId ?? 0
  const resolvedNotificationStrategy =
    member.notificationStrategy ?? member.memberNotificationStrategy ?? 0

  if (
    shouldEmitDebugWarnings &&
    member.groupId !== undefined &&
    member.memberGroupId !== undefined &&
    member.groupId !== member.memberGroupId
  ) {
    console.debug(
      `[membersService] Conflicting groupId for memberId=${member.memberId}: groupId=${member.groupId}, memberGroupId=${member.memberGroupId}. Using groupId.`
    )
  }

  if (
    shouldEmitDebugWarnings &&
    member.notificationStrategy !== undefined &&
    member.memberNotificationStrategy !== undefined &&
    member.notificationStrategy !== member.memberNotificationStrategy
  ) {
    console.debug(
      `[membersService] Conflicting notificationStrategy for memberId=${member.memberId}: notificationStrategy=${member.notificationStrategy}, memberNotificationStrategy=${member.memberNotificationStrategy}. Using notificationStrategy.`
    )
  }

  return {
    memberId: member.memberId ?? 0,
    memberFirstName: member.memberFirstName ?? '',
    memberEmail: member.memberEmail ?? member.email ?? '',
    memberPhoneNumber: member.memberPhoneNumber ?? member.phoneNumber ?? 0,
    memberPositionName: member.memberPositionName ?? member.positionName ?? '',
    memberComment: member.memberComment ?? member.comment ?? '',
    groupName: member.groupName ?? member.memberGroupName ?? '',
    groupId: resolvedGroupId,
    shopsRelated: member.shopsRelated ?? [],
    notificationStrategy: resolvedNotificationStrategy,
    notificationStrategyName:
      member.notificationStrategyName ?? member.memberNotificationStrategyName ?? '',
    canUpdateMember: Boolean(member.canUpdateMember),
    email: member.email ?? member.memberEmail,
    phoneNumber: member.phoneNumber ?? member.memberPhoneNumber,
    allowPhone: member.allowPhone,
    allowSms: member.allowSms,
    allowWhatsapp: member.allowWhatsapp,
    allowEmail: member.allowEmail,
    positionName: member.positionName ?? member.memberPositionName,
    comment: member.comment ?? member.memberComment,
  }
}

const membersService = {
  async getMembersList(): Promise<Member[]> {
    const path = '/notification/GetMembers'
    const store = useAppStore()
    
    const result = await request.get<RawMember[]>(import.meta.env.VITE_REVENUE_API_URL + path)
    const normalizedMembers = Array.isArray(result) ? result.map(normalizeMember) : []

    if (Array.isArray(result)) {
      store.setMembersList(normalizedMembers)
    }

    return normalizedMembers
  },

  async postMember(body: Partial<Member>): Promise<Response> {
    const path = '/auth/AddOrUpdateMember'
    return await request.post(import.meta.env.VITE_REVENUE_API_URL + path, body)
  },

  validateMemberData(member: Partial<Member>, fields: any[]): string[] {
    const errors: string[] = []
    const fieldsValidationResult = validationUtil.checkFieldValidity(fields)

    const {
      phoneNumber,
      email,
      notificationStrategy,
      groupId,
      allowPhone,
      allowSms,
      allowWhatsapp,
      allowEmail,
    } = member
    const mailFormatValid = email ? textHelpers.emailValidator(email) : true
    let validPhone = false
    
    if (phoneNumber && phoneNumber > 0) {
      const phoneNumberResult = parsePhoneNumberFromString(phoneNumber.toString(), 'IL')
      validPhone = phoneNumberResult ? phoneNumberResult.isValid() : false
    }

    if (!fieldsValidationResult) {
      errors.push('FormInvalid')
    }
    if ((!phoneNumber || phoneNumber <= 0) && !email) {
      errors.push('MissingEmailAndPhone')
    }
    if (phoneNumber && phoneNumber > 0 && !validPhone) {
      errors.push('MemberPhoneNumberInvalid')
    }
    if (email && !mailFormatValid) {
      errors.push('EmailInvalid')
    }
    if (!groupId) {
      errors.push('MissingNotificationGroup')
    }
    if (!notificationStrategy) {
      errors.push('MissingNotificationStrategy')
    }
    if (!email && allowEmail) {
      errors.push('MissingEmailForNotifyEmail')
    }
    if ((!phoneNumber || phoneNumber <= 0) && (allowPhone || allowSms || allowWhatsapp)) {
      errors.push('MissingPhoneForNotifyPhone')
    }

    return errors
  },

  prepareMembersDetails(
    membersList: Partial<Member>[],
    notificationGroupList: NotificationGroup[],
    strategiesList: Strategy[]
  ): Member[] {
    const distinctMembersList: Member[] = []
    
    if (membersList) {
      membersList.forEach((element) => {
        const memberId = element.memberId ?? 0
        const groupId = element.groupId ?? 0
        const notificationStrategy = element.notificationStrategy ?? 0
        const memberExists = distinctMembersList.find((g) => g.memberId === memberId)
        
        const relatedNotificationGroup = notificationGroupList?.filter(
          (g) => g.groupId === groupId
        )
        
        const relatedStrategy = strategiesList?.filter(
          (g) => g.id === notificationStrategy
        )
        
        const existingNotificationGroup = relatedNotificationGroup?.[0] || null
        const existingStrategy = relatedStrategy?.[0] || null
        
        if (memberExists === undefined) {
          distinctMembersList.push({
            memberId: memberId,
            memberFirstName: element.memberFirstName ?? '',
            memberEmail: element.email ?? element.memberEmail ?? '',
            memberPhoneNumber: element.phoneNumber ?? element.memberPhoneNumber ?? 0,
            memberPositionName: element.positionName ?? element.memberPositionName ?? '',
            memberComment: element.comment ?? element.memberComment ?? '',
            groupName: existingNotificationGroup?.groupName ?? element.groupName ?? '-',
            groupId: groupId,
            shopsRelated: existingNotificationGroup?.shopIds ?? [],
            notificationStrategy: notificationStrategy,
            notificationStrategyName:
              existingStrategy?.name ?? element.notificationStrategyName ?? '-',
            canUpdateMember: Boolean(element.canUpdateMember),
          })
        }
      })
    }
    return distinctMembersList
  },

  canCreateMember(membersList: Member[]): boolean {
    return membersList?.some((member) => member.canUpdateMember) ?? false
  },
}

export default membersService
